import type { ChatEntryWithSubTables } from "../../shared/aiSdkMessages";
import { CLIENT_AUTO_RESOLVED_TOOLS } from "../../shared/clientAutoResolvedTools";
import { isLoadedSkillNotice } from "../../shared/skills/attachedSkills";
import { SUB_AGENT_WAIT_TOOLS } from "../../shared/subAgent";

function isConversationalEntry(entry: ChatEntryWithSubTables) {
  return entry.entryType !== "Compaction" && !isLoadedSkillNotice(entry);
}

// Only the user ends a wait, by answering (which completes the call), typing
// something else, or interrupting. Everything else — a compaction, a "Saved:"
// checkpoint minted by a git sync, a workflow edit, more of the model's own turn
// — can land after the pending call while the wait still stands.
const WAIT_ENDING_ENTRY_TYPES = new Set(["UserMessage", "UserInterrupt"]);

function isPendingToolUse(entry: ChatEntryWithSubTables | undefined) {
  return (
    entry?.entryType === "AssistantToolUse" &&
    entry.toolCall != null &&
    entry.toolCall.completedAt == null
  );
}

export type StatusState =
  | "waiting"
  | "building"
  | "thinking"
  | "loading"
  | "resting";

export function getStatusState(
  entries: ChatEntryWithSubTables[],
  isResponding: boolean,
  runStatus: "idle" | "running",
  awaitingAction: boolean,
  pendingActionStatus: "building" | "waiting" = "waiting"
): StatusState {
  const lastEntry = entries.findLast(isConversationalEntry);
  if (!lastEntry) return "resting";
  if (lastEntry.entryType === "UserInterrupt") return "resting";
  // "Waiting" is driven by the server-set awaitingAction flag (the same signal
  // the workflow list uses), not a client guess from an incomplete tool call.
  // A tool that auto-resolves server-side (e.g. connectStepToApp) never sets it, so
  // its in-flight window can't flash the connect prompt.
  // An interrupt leaves its in-flight call incomplete forever, so anchor to the
  // newest call that is still the live one.
  const pendingEntry = entries.findLast(
    (e) => isPendingToolUse(e) || WAIT_ENDING_ENTRY_TYPES.has(e.entryType)
  );
  if (awaitingAction && isPendingToolUse(pendingEntry))
    return pendingActionStatus;
  if (
    isPendingToolUse(pendingEntry) &&
    SUB_AGENT_WAIT_TOOLS.has(pendingEntry!.toolCall!.toolName)
  )
    return "building";
  if (
    lastEntry.entryType === "AssistantToolUse" &&
    lastEntry.toolCall &&
    lastEntry.toolCall.completedAt == null &&
    CLIENT_AUTO_RESOLVED_TOOLS.has(lastEntry.toolCall.toolName)
  )
    return "building";
  if (runStatus === "running") {
    if (lastEntry.entryType === "AssistantToolUse") return "building";
    if (lastEntry.entryType === "AssistantMessage") return "building";
    return "thinking";
  }
  if (lastEntry.entryType === "UserMessage")
    return isResponding ? "thinking" : "resting";
  if (
    lastEntry.entryType === "AssistantReasoning" &&
    !lastEntry.assistantReasoning?.signature
  )
    return "thinking";
  if (lastEntry.entryType === "AssistantMessage" && isResponding)
    return "thinking";
  if (lastEntry.entryType === "AssistantToolUse" && isResponding)
    return "building";
  if (isResponding) return "loading";
  return "resting";
}
